import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import CircularProgress from "@mui/material/CircularProgress";
import React, { FC, useMemo, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { lightScrollbarStyle } from "../../styles/lightScrollbarStyle";
import { useSnackbar } from "../../components/Snackbar";
import { useCompanyTabs } from "./hooks/useCompanyTabs";
import { CompanyTab, TAB_DEFINITIONS } from "./types";

const TAB_TEST_IDS: Record<CompanyTab, string> = {
  [CompanyTab.SUMMARY]: "company-detail-tab-summary",
  [CompanyTab.SPEND]: "company-detail-tab-spend",
  [CompanyTab.INSTALL]: "company-detail-tab-install",
  [CompanyTab.CONTRACT]: "company-detail-tab-contract",
  [CompanyTab.INTENT]: "company-detail-tab-intent",
};
import { CompanyBreadcrumbs } from "./components/CompanyBreadcrumbs";
import { TooltipTab } from "./components/TooltipTab";
import { SummaryView } from "./views/SummaryView";
import {
  useCompanyPageStore,
  InstallFilters,
  IntentFilters,
} from "../../store/companyPageStore";
import { SpendView } from "./views/SpendView";
import { ContractView } from "./views/ContractView";
import { InstallView } from "./views/InstallView";
import { IntentView } from "./views/IntentView";
import { CompanyHeader } from "./views/components/Summary/CompanyHeader";
import { OrganizationView } from "./components/OrganizationView/OrganizationView";
import { IntentSignalDetails } from "./views/components/Intent/IntentSignalDetails";
import CreditProgressBar from "../../components/Credits/CreditsProgressBar";
import { CompanyDrawerHeader } from "./components/CompanyDrawerHeader";
import { TABS_ORDER } from "./constants";
import { useFeatureFlag } from "../../hooks/useLaunchDarkly";
import { useCommandPaletteStore } from "@/store/commandPalette/commandPaletteStore";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`company-page-tabpanel-${index}`}
      aria-labelledby={`company-page-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </Box>
  );
}

function a11yProps(index: number) {
  return {
    id: `company-page-tab-${index}`,
    "aria-controls": `company-page-tabpanel-${index}`,
  };
}

import type { SpogCompany } from "@/legacy/types/SpogCompany";

interface CompanyPageProps {
  companyId?: string;
  onClose?: () => void;
  installFilters?: InstallFilters;
  intentFilters?: IntentFilters;
  companyScores?: SpogCompany["scores"];
}

export const CompanyPage: FC<CompanyPageProps> = ({
  companyId: propCompanyId,
  onClose,
  installFilters,
  intentFilters,
  companyScores,
}) => {
  const { companyId: routeCompanyId } = useParams<{ companyId: string }>();
  const companyId = propCompanyId || routeCompanyId;
  const { showError } = useSnackbar();
  const addRecentItem = useCommandPaletteStore((s) => s.addRecentItem);
  const company = useCompanyPageStore((state) => state.company);
  const error = useCompanyPageStore((state) => state.error);
  const fetchCompany = useCompanyPageStore((state) => state.fetchCompany);
  const hasSpendData = useCompanyPageStore((state) => state.hasSpendData);
  const hasInstallData = useCompanyPageStore((state) => state.hasInstallData);
  const orgTreeLoading = useCompanyPageStore(
    (state) => state.loadingStates.orgTree
  );
  const organizationViewOpen = useCompanyPageStore(
    (state) => state.organizationViewOpen
  );
  const drawerMode = useCompanyPageStore((state) => state.drawerMode);
  const loadingStates = useCompanyPageStore((state) => state.loadingStates);
  const setCompanyScores = useCompanyPageStore(
    (state) => state.setCompanyScores
  );

  // Store company scores so CompanyHeader can read them
  useEffect(() => {
    setCompanyScores(companyScores);
  }, [companyScores, setCompanyScores]);

  const { activeTab, handleTabChange } = useCompanyTabs(companyId!, drawerMode);

  const intentFiltersEnabled = useFeatureFlag(
    "marketAnalyzerIntentFiltersEnabled",
    false
  );

  const visibleTabs = useMemo(
    () =>
      intentFiltersEnabled
        ? TABS_ORDER
        : TABS_ORDER.filter((tab) => tab !== CompanyTab.INTENT),
    [intentFiltersEnabled]
  );

  const getTabState = useCallback(
    (tab: CompanyTab) => {
      switch (tab) {
        case CompanyTab.SPEND:
          return {
            disabled: !hasSpendData,
            tooltip: !hasSpendData
              ? "No Spend information for this company at this time"
              : "",
          };
        case CompanyTab.INSTALL:
          return {
            disabled: !hasInstallData,
            tooltip: !hasInstallData
              ? "No Install information for this company at this time"
              : "",
          };
        default:
          return {
            disabled: false,
            tooltip: "",
          };
      }
    },
    [hasSpendData, hasInstallData]
  );

  useEffect(() => {
    if (companyId) {
      fetchCompany(companyId, installFilters, intentFilters);
    }
    // installFilters/intentFilters are seed values for the fetch — including
    // them as deps would loop because parents recreate the object each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, fetchCompany]);

  useEffect(() => {
    if (company?.hgId && company?.name) {
      addRecentItem({
        id: company.hgId,
        type: "company",
        name: company.name,
        description: company.url,
        route: `/catalog/companies/${company.hgId}`,
      });
    }
    // Note: company.url intentionally omitted - only track visits to new companies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.hgId, company?.name, addRecentItem]);

  const companyName = company?.name;

  const tabSlotProps = useMemo(() => ({ indicator: { sx: { top: 0 } } }), []);

  if (error) {
    showError("Error loading company data. Please try again later.");
    return <p>Error loading company data.</p>;
  }

  return (
    <Box display="flex" height="100%" position="relative" overflow="hidden">
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          transition: "margin-right 200ms ease-out",
          marginRight: organizationViewOpen ? "316px" : "0",
        }}
      >
        <Stack id="company-page" direction="column" height="100%">
          {drawerMode && onClose && <CompanyDrawerHeader onClose={onClose} />}
          <Stack
            direction="column"
            gap={2}
            paddingX={drawerMode ? 3 : 4}
            paddingTop={drawerMode ? 0 : 4}
          >
            {!drawerMode && <CompanyBreadcrumbs companyName={companyName} />}
            <Box borderTop={1} borderColor="divider">
              <Box display="flex" alignItems="center">
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  aria-label="company page tabs"
                  slotProps={tabSlotProps}
                  sx={{ flexGrow: 1 }}
                >
                  {visibleTabs.map((tab) => {
                    const { disabled, tooltip } = getTabState(tab);
                    const isLoading =
                      (tab === CompanyTab.SPEND && loadingStates.spendData) ||
                      (tab === CompanyTab.INSTALL && loadingStates.installData);

                    return (
                      // HACK: TooltipTab is a hack to get the tooltip to work with the Tab component.
                      <TooltipTab
                        key={tab}
                        data-testid={TAB_TEST_IDS[tab]}
                        label={
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {TAB_DEFINITIONS[tab].label}
                            {isLoading && (
                              <CircularProgress
                                size={14}
                                thickness={4}
                                sx={{ color: "text.secondary" }}
                              />
                            )}
                          </Box>
                        }
                        disabled={disabled || isLoading}
                        tooltip={isLoading ? "Loading..." : tooltip}
                        {...a11yProps(tab)}
                      />
                    );
                  })}
                </Tabs>
              </Box>
              <Box display="flex" justifyContent="flex-end">
                <CreditProgressBar />
              </Box>
            </Box>
          </Stack>

          <Stack
            direction="column"
            flexGrow={1}
            paddingX={drawerMode ? 3 : 4}
            paddingBottom={4}
            overflow="auto"
            sx={lightScrollbarStyle}
          >
            <Box marginY={2}>
              <CompanyHeader />
            </Box>
            <Box>
              <TabPanel value={activeTab} index={CompanyTab.SUMMARY}>
                <SummaryView />
              </TabPanel>
              <TabPanel value={activeTab} index={CompanyTab.SPEND}>
                {hasSpendData && <SpendView />}
              </TabPanel>
              <TabPanel value={activeTab} index={CompanyTab.INSTALL}>
                {hasInstallData && <InstallView />}
              </TabPanel>
              <TabPanel value={activeTab} index={CompanyTab.CONTRACT}>
                <ContractView />
              </TabPanel>
              {intentFiltersEnabled && (
                <TabPanel value={activeTab} index={CompanyTab.INTENT}>
                  <IntentView />
                </TabPanel>
              )}
            </Box>
          </Stack>
        </Stack>
      </Box>

      {!orgTreeLoading && <OrganizationView />}
      {intentFiltersEnabled && <IntentSignalDetails />}
    </Box>
  );
};
